import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Forms
import gameFormatService from '../../features/gameFormat/gameFormatService';

//UI
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useTheme
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PrimaryButton from '../ui/buttons/primaryButton';
import { generateTeamName } from '../../helpers/teams';
import { v4 as uuidv4 } from 'uuid';

import eventService from '../../features/event/eventService';
import { setEvent, updateEventTeam } from '../../features/event/eventSlice';

const TeamDialog = ({ open, setOpen, selectedTeam }) => {
  const dispatch = useDispatch();
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamReserves, setTeamReserves] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedMember, setSelectedMember] = useState({});
  const [selectedReserve, setSelectedReserve] = useState(null);
  const [error, setError] = useState(null);
  const [reserveError, setReserveError] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [teamGroups, setTeamGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [groupError, setGroupError] = useState(null);

  const theme = useTheme();
  const event = useSelector((state) => state.event.event);
  const useSides = Boolean(event.useGroups);

  useEffect(() => {
    if (selectedTeam) {
      setTeamMembers(Array.isArray(selectedTeam.members) ? selectedTeam.members : []);
      setTeamReserves(Array.isArray(selectedTeam.reserves) ? selectedTeam.reserves : []);
      setTeamName(selectedTeam.name || '');
      setSelectedGroupId(selectedTeam.groupId || '');
      return;
    }

    setTeamMembers([]);
    setTeamReserves([]);
    setTeamName('');
    setSelectedGroupId('');
  }, [selectedTeam]);

  useEffect(() => {
    if (event.players) {
      setPlayers(
        event.players.map((player) => ({
          label: `${player.name} ${player.surname}`,
          value: player.id
        }))
      );
    }

    setTeamGroups(Array.isArray(event.teamGroups) ? event.teamGroups : []);

    return () => {
      setTeamMembers([]);
      setTeamReserves([]);
      setPlayers([]);
      setTeamGroups([]);
      setSelectedGroupId('');
    };
  }, [event]);

  const handleAddTeamMember = (member) => {
    if (member) {
      const player = event.players.find((p) => p.id === member.value);
      setSelectedMember(player);
    }
  };

  const handleAddReserve = (reserve) => {
    if (reserve) {
      const player = event.players.find((p) => p.id === reserve.value);
      setSelectedReserve(player);
    }
  };

  const handleOnAdd = (target) => {
    if (target.code === 'Enter' && selectedMember) {
      setError(null);
      const limit = gameFormatService.getGameTypeLimit(event.gameFormat.gameType) / 2;

      const existsInList = teamMembers.some((tm) => tm.id === selectedMember.id);
      const limitReached = teamMembers.length >= limit;

      if (existsInList) {
        setError('Already in the lineup.');
      } else if (limitReached) {
        setError('Limit Reached.');
      } else {
        // A player on the bench joins the lineup instead of being duplicated.
        setTeamMembers((prevMembers) => [...prevMembers, selectedMember]);
        setTeamReserves((prevReserves) =>
          prevReserves.filter((reserve) => reserve.id !== selectedMember.id)
        );
        setSelectedMember(null);
      }
    }
  };

  const handleOnAddReserve = (target) => {
    if (target.code === 'Enter' && selectedReserve) {
      setReserveError(null);
      const existsInList = teamReserves.some((r) => r.id === selectedReserve.id);

      if (existsInList) {
        setReserveError('Already in reserves.');
      } else {
        // Moving an active player to the bench is allowed but never duplicates.
        setTeamReserves((prevReserves) => [...prevReserves, selectedReserve]);
        setTeamMembers((prevMembers) =>
          prevMembers.filter((member) => member.id !== selectedReserve.id)
        );
        setSelectedReserve(null);
      }
    }
  };

  const hanldeRemoveTeamMember = (player) => {
    setTeamMembers(teamMembers.filter((tm) => tm.id !== player.id));
  };

  const handleRemoveReserve = (player) => {
    setTeamReserves(teamReserves.filter((reserve) => reserve.id !== player.id));
  };

  const handleCreateTeam = () => {
    const resolvedTeamName = teamName.trim() || generateTeamName(event.gameFormat.gameType, teamMembers);

    if (selectedTeam) {
      const updatedTeam = {
        id: selectedTeam.id,
        members: teamMembers,
        reserves: teamReserves,
        name: resolvedTeamName,
        groupId: selectedGroupId || null
      };

      dispatch(updateEventTeam({ eventId: event.id, team: updatedTeam }));
      //eventService.updateTeam(event.id, team);
      setOpen(false);
    } else {
      const team = {
        members: teamMembers,
        name: resolvedTeamName,
        groupId: selectedGroupId || null,
        ...(teamReserves.length > 0 ? { reserves: teamReserves } : {})
      };

      eventService.addTeam(event.id, team);
      setOpen(false);
    }
  };

  const handleOpenGroupDialog = () => {
    setGroupError(null);
    setNewGroupName('');
    setGroupDialogOpen(true);
  };

  const handleCreateGroup = async () => {
    const trimmedName = newGroupName.trim();
    if (!trimmedName) {
      setGroupError('Side name is required.');
      return;
    }

    const exists = teamGroups.some(
      (group) =>
        String(group.name || '')
          .trim()
          .toLowerCase() === trimmedName.toLowerCase()
    );

    if (exists) {
      setGroupError('Side name already exists.');
      return;
    }

    const group = { id: uuidv4(), name: trimmedName };
    const nextGroups = [...teamGroups, group];
    await eventService.addTeamGroup(event.id, group);
    setTeamGroups(nextGroups);
    setSelectedGroupId(group.id);
    dispatch(setEvent({ ...event, teamGroups: nextGroups }));
    setGroupDialogOpen(false);
  };

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="xs" onClose={() => setOpen(false)}>
        <DialogTitle>Select Team Members</DialogTitle>
        <DialogContent>
          <Box display={'flex'} flexDirection={'column'}>
            {useSides ? (
              <Box display="flex" alignItems="center" gap={1} sx={{ pt: 1 }}>
                <TextField
                  select
                  fullWidth
                  label="Select Side"
                  value={selectedGroupId}
                  onChange={(event) => setSelectedGroupId(event.target.value)}>
                  <MenuItem value="">No side</MenuItem>
                  {teamGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" onClick={handleOpenGroupDialog}>
                  New Side
                </Button>
              </Box>
            ) : null}
            <TextField
              fullWidth
              label="Team Name"
              value={teamName}
              onChange={(event) => setTeamName(event.target.value)}
              helperText="Leave blank to use the generated team name"
              sx={{ pt: 1 }}
            />
            <Typography variant="subtitle2" sx={{ pt: 2 }}>
              Lineup
            </Typography>
            <Autocomplete
              sx={{ pt: 1 }}
              disablePortal
              id="team-a-player-search"
              options={players}
              onChange={(event, value) => handleAddTeamMember(value)}
              onKeyUp={(event) => handleOnAdd(event)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Start Typing"
                  helperText={error ? error : 'Press Enter to add'}
                  color={error ? 'error' : 'info'}
                />
              )}
            />

            <Paper
              sx={{
                backgroundColor: theme.palette.background.default,
                mt: 1,
                minHeight: '150px'
              }}>
              <List>
                {teamMembers.length > 0 ? (
                  teamMembers.map((member) => (
                    <ListItem
                      key={member.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          color="error"
                          onClick={() => hanldeRemoveTeamMember(member)}>
                          <DeleteIcon />
                        </IconButton>
                      }>
                      {member.name} {member.surname}
                    </ListItem>
                  ))
                ) : (
                  <Typography m={2} variant={'caption'}>
                    There is no players in this team yet...
                  </Typography>
                )}
              </List>
            </Paper>

            <Typography variant="subtitle2" sx={{ pt: 2 }}>
              Reserves (optional)
            </Typography>
            <Autocomplete
              sx={{ pt: 1 }}
              disablePortal
              id="team-a-reserve-search"
              options={players}
              onChange={(event, value) => handleAddReserve(value)}
              onKeyUp={(event) => handleOnAddReserve(event)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add a reserve"
                  helperText={reserveError ? reserveError : 'Press Enter to add'}
                  color={reserveError ? 'error' : 'info'}
                />
              )}
            />

            <Paper
              sx={{
                backgroundColor: theme.palette.background.default,
                mt: 1,
                minHeight: '80px'
              }}>
              <List>
                {teamReserves.length > 0 ? (
                  teamReserves.map((reserve) => (
                    <ListItem
                      key={reserve.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="remove reserve"
                          color="error"
                          onClick={() => handleRemoveReserve(reserve)}>
                          <DeleteIcon />
                        </IconButton>
                      }>
                      {reserve.name} {reserve.surname}
                    </ListItem>
                  ))
                ) : (
                  <Typography m={2} variant={'caption'}>
                    No reserves. Reserves do not play until promoted.
                  </Typography>
                )}
              </List>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          {teamMembers && teamMembers.length > 0 ? (
            <Typography variant="caption">
              {generateTeamName(event.gameFormat.gameType, teamMembers)}
            </Typography>
          ) : null}
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <PrimaryButton onClick={() => handleCreateTeam()} variant={'contained'}>
            {selectedTeam ? 'Update Team' : 'Create Team'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)}>
        <DialogTitle>Create Side</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Side name"
            fullWidth
            value={newGroupName}
            onChange={(event) => setNewGroupName(event.target.value)}
            error={Boolean(groupError)}
            helperText={groupError || 'Enter a name for this side'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
          <PrimaryButton onClick={handleCreateGroup} variant={'contained'}>
            Create Side
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamDialog;
